// Can't-find report (Fernando, 2026-09-03): select orders in Can't find /
// Hard to find, click Report — per marked-not-found item: article, picking
// list, shelf, remaining stock, then a sales-history table per store that
// drains that stock (Nora alone; Big Oliver + Motley Denim together, because
// they sell the same WGR stock). The computed stock column walks ONE combined
// timeline across the sharing stores, so the level is right even though the
// display is split per store. WMS rows are our own stock writes (returns /
// exchange corrections) — known additions sales can't explain.
import { useEffect, useState } from 'react';
import { API, authFetch } from './api';
import { titleEn } from './lineName';

export default function CantFindReport({ ids, storeNames, onClose }:
  { ids: string[]; storeNames: Record<string, string>; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await authFetch(`${API}/orders/cant-find-report.xlsx`, {
        method: 'POST', body: JSON.stringify({ ids }),
      });
      if (!res.ok) { setError('Excel export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers.get('Content-Disposition') || '').match(/filename=(.+)/)?.[1]
                   || 'cant_find_report.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  useEffect(() => {
    authFetch(`${API}/orders/cant-find-report`, {
      method: 'POST', body: JSON.stringify({ ids }),
    }).then(async r => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      return r.json();
    }).then(setData).catch(e => setError(e.message));
  }, [ids]);

  const store = (id: string) => storeNames[id] || id;
  const dt = (s: string | null) => (s ? new Date(s).toLocaleString() : '—');

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-[80vw] bg-[#0f1117] border-l border-gray-800 flex flex-col overflow-hidden">
        <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-xl">Can't find report</h2>
            {data && (
              <p className="text-gray-500 text-xs mt-0.5">
                {data.items.length} item{data.items.length !== 1 ? 's' : ''} marked not found
                across {ids.length} selected order{ids.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportXlsx} disabled={exporting || !data || data.items.length === 0}
              className="text-white text-sm px-3 py-1.5 bg-green-700 hover:bg-green-600 disabled:opacity-40 rounded-lg">
              {exporting ? 'Exporting…' : '⬇ Export Excel'}
            </button>
            <button onClick={onClose}
              className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">
              ✕ Close
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && <div className="text-red-400 text-sm">{error}</div>}
          {!data && !error && <div className="text-gray-500 text-sm">Building report…</div>}

          {data && data.items.length === 0 && (
            <div className="text-gray-500 text-sm py-10 text-center border border-dashed border-gray-800 rounded-lg">
              None of the selected orders has items marked not found (the red ? / ✕ marker
              in the order panel). Mark the missing items first, then run the report.
            </div>
          )}

          {data && data.items.map((it: any, i: number) => (
            <div key={i} className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
              {/* the four columns Fernando asked for, plus context */}
              <div className="px-5 py-3 border-b border-gray-800">
                <div className="text-white font-medium">{titleEn(it)}</div>
                <div className="text-gray-500 text-xs mt-0.5">
                  from order{it.from_orders.length !== 1 ? 's' : ''} {it.from_orders.join(', ')}
                  {' · '}stock: {it.stock_owner}
                  {it.not_in_wgr && <span className="text-amber-400"> · NOT FOUND IN WGR — stock figures unavailable</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 text-sm border-b border-gray-800">
                <div className="px-5 py-2.5"><div className="text-gray-500 text-xs">Article number</div>
                  <div className="text-white font-mono">{it.article_number || '—'}</div></div>
                <div className="px-5 py-2.5"><div className="text-gray-500 text-xs">Picking list</div>
                  <div className="text-gray-200">{it.picklist || '—'}</div></div>
                <div className="px-5 py-2.5"><div className="text-gray-500 text-xs">Stock location</div>
                  <div className="text-blue-400 font-mono">{it.stockplace || '—'}</div></div>
                <div className="px-5 py-2.5"><div className="text-gray-500 text-xs">Remaining stock</div>
                  <div className="text-white font-semibold">{it.remaining_stock ?? '—'}</div></div>
                <div className="px-5 py-2.5"><div className="text-gray-500 text-xs">Missing</div>
                  <div className="text-red-400 font-semibold">{it.missing ?? '—'}</div></div>
              </div>

              {it.stores_history.map((sh: any) => (
                <div key={sh.webshop_id} className="border-b border-gray-800 last:border-0">
                  <div className="px-5 pt-3 pb-1 text-gray-400 text-xs uppercase tracking-wider font-semibold">
                    {store(sh.webshop_id)}
                  </div>
                  {sh.not_sold_here ? (
                    <div className="px-5 pb-3 text-gray-600 text-sm">Not sold in this store.</div>
                  ) : sh.rows.length === 0 ? (
                    <div className="px-5 pb-3 text-gray-600 text-sm">No movements in the window.</div>
                  ) : (
                    <table className="w-full text-sm mb-2">
                      <thead>
                        <tr className="text-gray-600 text-xs">
                          <th className="px-5 py-1 text-left font-normal">Date</th>
                          <th className="px-5 py-1 text-left font-normal">Order / event</th>
                          <th className="px-5 py-1 text-right font-normal">Qty</th>
                          <th className="px-5 py-1 text-left font-normal">State</th>
                          <th className="px-5 py-1 text-right font-normal">Stock after</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sh.rows.map((r: any, j: number) => (
                          <tr key={j} className="border-t border-gray-800/50">
                            <td className="px-5 py-1.5 text-gray-400 whitespace-nowrap">{dt(r.at)}</td>
                            <td className="px-5 py-1.5">
                              {r.kind === 'wms'
                                ? <span className="text-purple-300">{r.ref}</span>
                                : <span className="text-gray-200">{r.ref}</span>}
                            </td>
                            <td className={`px-5 py-1.5 text-right font-mono ${r.delta > 0 ? 'text-green-400' : 'text-gray-300'}`}>
                              {r.delta > 0 ? `+${r.delta}` : r.delta}
                            </td>
                            <td className="px-5 py-1.5 text-gray-500">{r.tab || ''}</td>
                            <td className="px-5 py-1.5 text-right font-mono text-gray-200">{r.level_after ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
              <div className="px-5 py-2 text-gray-600 text-xs">{it.history_note}</div>
            </div>
          ))}

          {data && (
            <p className="text-gray-600 text-xs">{data.note}</p>
          )}
        </div>
      </div>
    </div>
  );
}
